import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Scissors, Download, ArrowLeft, CheckCircle, Upload, FileText, X, Shield, Zap, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';
import JSZip from 'jszip';

// Configure PDF.js worker - using local worker file from public directory
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SplitPage = () => {
  const { toast } = useToast();
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [splitType, setSplitType] = useState('pages');
  const [pageNumbers, setPageNumbers] = useState('');
  const [startPage, setStartPage] = useState('');
  const [endPage, setEndPage] = useState('');
  const [loading, setLoading] = useState(false);
  const [splitResults, setSplitResults] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const fileInputRef = useRef(null);

  // Check if bookmark banner was previously dismissed
  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('split-pdf-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to get PDF page count
  const getPdfPageCount = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (error) {
      console.error('Error reading PDF:', error);
      return 0;
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: "Error",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Maximum 10MB allowed",
        variant: "destructive",
      });
      return;
    }

    // Show loading state
    setLoading(true);

    try {
      // Get actual page count from PDF
      console.log('Reading PDF page count...');
      const pageCount = await getPdfPageCount(file);
      console.log('PDF page count:', pageCount);

      // Upload to backend
      const formData = new FormData();
      formData.append('file', file);

      const uploadUrl = `${BACKEND_URL}/api/pdf/upload?session_id=${sessionId}`;
      console.log('Uploading to:', uploadUrl);

      const uploadResponse = await apiClient.post(
        uploadUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('Upload response:', uploadResponse.data);

      const pdfData = {
        id: uploadResponse.data.id,
        file: file,
        name: file.name,
        size: uploadResponse.data.size,
        pages: uploadResponse.data.pages,
        uploadDate: uploadResponse.data.upload_date,
        preview: URL.createObjectURL(file)
      };

      setUploadedPdf(pdfData);
      setSplitResults([]);
      
      toast({
        title: "Success",
        description: "PDF uploaded successfully",
      });
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to upload PDF",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const removePdf = () => {
    if (uploadedPdf?.preview) {
      URL.revokeObjectURL(uploadedPdf.preview);
    }
    setUploadedPdf(null);
    setSplitResults([]);
    setPageNumbers('');
    setStartPage('');
    setEndPage('');
  };

  const handleSplit = async () => {
    if (!uploadedPdf) {
      toast({
        title: "Error",
        description: "Please upload a PDF to split",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      let splitRequest;
      
      if (splitType === 'pages') {
        const pages = pageNumbers.split(',').map(p => parseInt(p.trim())).filter(p => !isNaN(p));
        if (pages.length === 0) {
          throw new Error('Please enter valid page numbers');
        }
        
        // Validate page numbers against total pages
        const invalidPages = pages.filter(p => p < 1 || p > uploadedPdf.pages);
        if (invalidPages.length > 0) {
          throw new Error(`Invalid page numbers: ${invalidPages.join(', ')}. PDF has ${uploadedPdf.pages} pages.`);
        }
        
        splitRequest = {
          pdf_id: uploadedPdf.id,
          type: 'pages',
          pages: pages
        };
      } else {
        const start = parseInt(startPage);
        const end = parseInt(endPage);
        if (isNaN(start) || isNaN(end) || start > end) {
          throw new Error('Please enter valid page range');
        }
        
        if (start < 1 || end > uploadedPdf.pages) {
          throw new Error(`Page range must be between 1 and ${uploadedPdf.pages}`);
        }
        
        // Convert range to array of pages for individual file creation
        const pages = [];
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        splitRequest = {
          pdf_id: uploadedPdf.id,
          type: 'pages',
          pages: pages
        };
      }

      console.log('Split request:', splitRequest);
      console.log('Session ID:', sessionId);

      // Call backend split API
      const response = await apiClient.post(
        `${BACKEND_URL}/api/pdf/split?session_id=${sessionId}`,
        splitRequest,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      setSplitResults(response.data);
      
      toast({
        title: "Success",
        description: `PDF split into ${response.data.length} file(s)`,
      });
    } catch (error) {
      console.error('Split error:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: "Error",
        description: error.message || error.response?.data?.detail || "Failed to split PDF",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      // Download file from backend
      const response = await apiClient.get(
        `${BACKEND_URL}${file.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${file.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const handleDownloadAllAsZip = async () => {
    try {
      toast({
        title: "Creating ZIP",
        description: "Please wait while we prepare your files...",
      });

      const zip = new JSZip();
      
      // Download each file and add to zip
      for (let i = 0; i < splitResults.length; i++) {
        const file = splitResults[i];
        
        try {
          const response = await apiClient.get(
            `${BACKEND_URL}${file.download_url}?session_id=${sessionId}`,
            {
              responseType: 'arraybuffer',
            }
          );
          
          // Add file to zip
          zip.file(file.name, response.data);
        } catch (error) {
          console.error(`Failed to download ${file.name}:`, error);
        }
      }
      
      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create ZIP filename based on uploaded PDF name
      const uploadedFileName = uploadedPdf.name.replace('.pdf', '');
      const zipFileName = `${uploadedFileName}_AllSmallTools_Split_Zip.zip`;
      
      // Download zip file
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `Downloaded ZIP with ${splitResults.length} files`,
      });
    } catch (error) {
      console.error('Download all error:', error);
      toast({
        title: "Error",
        description: "Failed to create ZIP file",
        variant: "destructive",
      });
    }
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('split-pdf-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Split PDF Online — Free & Fast PDF Page Splitter | AllSmallTooLS</title>
        <meta name="description" content="Split PDF files into multiple pages instantly. 100% free, secure, and works in your browser — no sign-up or installation needed." />
        <meta name="keywords" content="split pdf, pdf splitter, online pdf tool, divide pdf pages, free pdf splitter" />
        <link rel="canonical" href="https://allsmalltools.com/split-pdf" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to PDF splitter anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your PDF splitter for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <Scissors className="h-8 w-8 text-red-500" />
            <span>Split PDF</span>
          </h1>
          <p className="text-gray-600">Extract specific pages or ranges from your PDF document</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Split Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Upload PDF & Split Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* PDF Upload */}
            {!uploadedPdf ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  dragActive 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={loading ? undefined : handleDrag}
                onDragLeave={loading ? undefined : handleDrag}
                onDragOver={loading ? undefined : handleDrag}
                onDrop={loading ? undefined : handleDrop}
                onClick={loading ? undefined : () => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={loading}
                />
                
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {loading ? 'Reading PDF...' : 'Upload PDF to Split'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {loading ? 'Please wait while we analyze your PDF' : 'Drag and drop your PDF file here, or click to browse'}
                </p>
                <p className="text-sm text-gray-500">
                  Maximum file size: 10MB • Supported format: PDF
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-16 bg-red-100 rounded flex items-center justify-center">
                    <FileText className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{uploadedPdf.name}</p>
                    <p className="text-sm text-gray-500">{uploadedPdf.pages} pages • {uploadedPdf.size}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removePdf}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Split Options */}
            {uploadedPdf && (
              <Tabs value={splitType} onValueChange={setSplitType}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="pages">Specific Pages</TabsTrigger>
                  <TabsTrigger value="range">Page Range</TabsTrigger>
                </TabsList>
                
                <TabsContent value="pages" className="space-y-3">
                  <Label htmlFor="pageNumbers">Page Numbers (comma-separated)</Label>
                  <Input
                    id="pageNumbers"
                    placeholder="e.g., 1, 3, 5, 7"
                    value={pageNumbers}
                    onChange={(e) => setPageNumbers(e.target.value)}
                  />
                  <p className="text-sm text-gray-500">
                    Enter the page numbers you want to extract (1-{uploadedPdf.pages})
                  </p>
                </TabsContent>
                
                <TabsContent value="range" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="startPage">Start Page</Label>
                      <Input
                        id="startPage"
                        type="number"
                        min="1"
                        max={uploadedPdf.pages}
                        value={startPage}
                        onChange={(e) => setStartPage(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="endPage">End Page</Label>
                      <Input
                        id="endPage"
                        type="number"
                        min="1"
                        max={uploadedPdf.pages}
                        value={endPage}
                        onChange={(e) => setEndPage(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Select a range from 1 to {uploadedPdf.pages}
                  </p>
                </TabsContent>
              </Tabs>
            )}

            <Button 
              onClick={handleSplit}
              disabled={!uploadedPdf || loading}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
            >
              {loading ? 'Splitting...' : 'Split PDF'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Split Results</CardTitle>
          </CardHeader>
          <CardContent>
            {splitResults.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {splitResults.length} file(s) created successfully
                  </p>
                  <Button
                    onClick={handleDownloadAllAsZip}
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download All as ZIP
                  </Button>
                </div>
                
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-3 uppercase font-semibold">Individual Pages</p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {splitResults.map((file, index) => (
                      <Card key={file.id}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-12 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                                <FileText className="h-5 w-5 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {file.pages} page • {file.size}
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleDownload(file)}
                              size="sm"
                              variant="outline"
                              className="flex-shrink-0"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Scissors className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No splits created yet</p>
                <p className="text-sm">Configure split settings and click "Split PDF"</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Split PDF Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Split PDF Tool?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-red-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Fast & Easy</h3>
              <p className="text-gray-600">
                Split PDF files in seconds with our intuitive online tool. No software installation needed. Just upload, select pages, and download your split PDFs instantly.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Secure & Private</h3>
              <p className="text-gray-600">
                Your PDF files are processed securely. We automatically delete all uploaded and split files after processing to protect your privacy and data security.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Multiple Split Options</h3>
              <p className="text-gray-600">
                Extract specific pages, split by page ranges, or divide your PDF into multiple documents. Flexible options for all your PDF splitting needs.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to extract a few pages from a large document, divide a PDF into chapters, or separate pages for sharing – our free PDF splitter makes it easy. Works with any PDF file size and preserves the original quality. Try our split PDF tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/merge-pdf" className="text-red-600 hover:text-red-800 text-sm font-medium">
                → Merge PDF Files
              </Link>
              <Link to="/compress-pdf" className="text-red-600 hover:text-red-800 text-sm font-medium">
                → Compress PDF
              </Link>
              <Link to="/lock-unlock-pdf" className="text-red-600 hover:text-red-800 text-sm font-medium">
                → Lock/Unlock PDF
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Split PDF Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Split PDF Files Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your PDF:</strong> Click the upload area or drag and drop your PDF file. Our PDF splitter supports files of any size.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Choose Split Method:</strong> Select how you want to split your PDF – by specific pages (e.g., 1,3,5), by page range (e.g., 1-10), or extract all pages separately.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Split PDF:</strong> Click the "Split PDF" button and wait a few seconds while our tool processes your document.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Split Files:</strong> Download your split PDF files individually or as a ZIP archive containing all extracted pages.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is this PDF splitter free to use?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our split PDF tool is completely free to use. You can split unlimited PDF files without any registration, watermarks, or hidden fees.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is it safe to split PDFs online?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Absolutely! We take security seriously. All files are processed securely and automatically deleted from our servers after processing. Your documents remain private and confidential.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I split password-protected PDFs?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">If your PDF is password-protected, you'll need to unlock it first using our Lock/Unlock PDF tool. Once unlocked, you can then split the PDF as needed.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What's the maximum file size I can split?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Our PDF splitter can handle large files. While we recommend files under 100MB for optimal performance, larger files may take a bit longer to process but will work just fine.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default SplitPage;
